import { ReloadOutlined } from "@ant-design/icons";
import { Button, Input, Select, Space } from "antd";
import type { ReactNode } from "react";

import { useNames } from "../../api/names";
import type { Group } from "../../api/types";

/** The candidate filters shared by the candidate and answer-sheet pages. */
export interface CandidateFilters {
  group?: Group;
  regionId?: number;
  siteId?: number;
  roomId?: number;
  enabled?: boolean;
  keyword?: string;
}

// antd Select values must be strings or numbers; booleans go through these.
const flag = (value: boolean | undefined) => value === undefined ? undefined : value ? "yes" : "no";
const unflag = (value: "yes" | "no" | undefined) => value === undefined ? undefined : value === "yes";

export function CandidateFilterBar({ value, onChange, refreshing, onRefresh, extra }: {
  value: CandidateFilters;
  onChange: (next: CandidateFilters) => void;
  refreshing?: boolean;
  onRefresh?: () => void;
  extra?: ReactNode;
}) {
  const { catalog } = useNames();
  const groups = catalog.data?.groups ?? [];
  const regions = catalog.data?.regions ?? [];
  const sites = (catalog.data?.sites ?? []).filter((site) => value.regionId === undefined || site.regionId === value.regionId);
  const rooms = (catalog.data?.rooms ?? []).filter((room) => room.siteId === value.siteId);
  const update = (patch: Partial<CandidateFilters>) => onChange({ ...value, ...patch });

  return (
    <Space wrap>
      {groups.length > 1 && (
        <Select<Group> allowClear placeholder="全部组别" style={{ width: 140 }} value={value.group}
          onChange={(group) => update({ group })}
          options={groups.map((item) => ({ value: item.id, label: item.name }))} />
      )}
      <Select<number> allowClear showSearch optionFilterProp="label" placeholder="全部考点区域" style={{ width: 160 }} value={value.regionId}
        onChange={(regionId) => update({ regionId, siteId: undefined, roomId: undefined })}
        options={regions.map((item) => ({ value: item.id, label: item.name }))} />
      <Select<number> allowClear showSearch optionFilterProp="label" placeholder="全部考点" style={{ width: 220 }} value={value.siteId}
        onChange={(siteId) => update({ siteId, roomId: undefined })}
        options={sites.map((item) => ({ value: item.id, label: `${item.name}（${item.id}）` }))} />
      <Select<number> allowClear showSearch optionFilterProp="label" placeholder="全部考场" style={{ width: 180 }} value={value.roomId} disabled={value.siteId === undefined}
        onChange={(roomId) => update({ roomId })}
        options={rooms.map((item) => ({ value: item.id, label: item.name }))} />
      <Select<"yes" | "no"> allowClear placeholder="账号状态" style={{ width: 120 }} value={flag(value.enabled)}
        onChange={(next) => update({ enabled: unflag(next) })}
        options={[{ value: "yes", label: "启用" }, { value: "no", label: "已关闭" }]} />
      <Input.Search allowClear placeholder="准考证号或姓名" style={{ width: 220 }} defaultValue={value.keyword}
        onSearch={(text) => update({ keyword: text.trim() || undefined })} />
      {onRefresh && <Button icon={<ReloadOutlined />} loading={refreshing} onClick={onRefresh}>刷新</Button>}
      {extra}
    </Space>
  );
}
